import { useState } from "react";
import { Send, Check, ExternalLink, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";

interface InlinePoptavkaBoxProps {
  defaultEmail?: string;
  defaultPhone?: string;
  onSubmit: (email: string, phone: string) => Promise<string | null>;
  /** Set externally when inquiry was already created (e.g. via LEAD_DATA fallback) */
  inquiryId?: string;
}

export function InlinePoptavkaBox({ defaultEmail, defaultPhone, onSubmit, inquiryId: externalInquiryId }: InlinePoptavkaBoxProps) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [phone, setPhone] = useState(defaultPhone || "");
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(externalInquiryId || null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Vyplňte e-mail");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const id = await onSubmit(email.trim(), phone.trim());
      if (id) setCreatedId(id);
    } catch {
      setError("Nepodařilo se vytvořit poptávku. Zkuste to znovu.");
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (createdId) {
    return (
      <div className="mt-3 rounded-xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
          <span className="text-sm font-semibold text-green-800 dark:text-green-300">Poptávka vytvořena!</span>
        </div>
        <p className="text-xs text-green-700 dark:text-green-400 mb-2">
          V detailu poptávky můžeš doplnit jméno, adresu stavby, fotky a další informace.
        </p>
        <Link
          to={`/dashboard/${createdId}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          Otevřít detail poptávky
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  // Form state
  return (
    <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
          <Send className="w-3.5 h-3.5 text-primary" />
        </div>
        <span className="text-sm font-semibold text-foreground">Chcete poptat tuto službu?</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Zadejte kontakt a my vám propojíme s dodavateli z vašeho okolí. Zdarma a nezávazně.
      </p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="email"
              placeholder="E-mail *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          <div className="relative flex-1">
            <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="tel"
              placeholder="Telefon"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <span className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-primary-foreground/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-primary-foreground/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-primary-foreground/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </span>
          ) : (
            <>
              <Send className="w-3.5 h-3.5" />
              Vytvořit poptávku
            </>
          )}
        </button>
      </form>
    </div>
  );
}
